import { pick } from 'lodash';
import * as yup from 'yup';

import { translations } from 'locales/i18n';

import { SubjectType } from '../../ModuleSelections/models';

export const moduleValidationSchema = t => ({
  attachments: yup.mixed(),
  description: yup.string().nullable(),
  moduleOfficer: yup.number().nullable(),
  lecturers: yup.array().of(yup.number().nullable()),
  unregisteredLecturers: yup.array().of(yup.string().nullable()),
  groupingId: yup.number().when('subjectType', {
    is: 'AWPM',
    then: yup
      .number()
      .required(t(translations.validation.required()))
      .nullable()
      .label(t(translations.pages.groupings.modal.groupingName()))
      .transform(value => (isNaN(value) ? null : value)),
    otherwise: yup.number().nullable()
  }),
  isMoveUpActive: yup.boolean(),
  isTakingPlace: yup.boolean(),

  maxParticipants: yup
    .number()
    .nullable()
    .transform(value => (isNaN(value) ? null : value)),
  minParticipants: yup
    .number()
    .nullable()
    .transform(value => (isNaN(value) ? null : value)),
  name: yup
    .string()
    .nullable()
    .required(t(translations.validation.required()))
    .label(t(translations.pages.modules.create.GeneralInfo.name())),
  participantsConfigurations: yup.array().of(
    yup.object().shape({
      custom: yup.boolean(),
      admissionCriteria: yup
        .array()
        .of(
          yup
            .string()
            .oneOf(['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'B8', 'B9', 'M1', 'M2', 'M3'])
        )
        .label(
          t(
            translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.admissionCriteria()
          )
        ),
      isGlobal: yup
        .boolean()
        .nullable()
        .transform(value => (!value ? false : true))
        .label(
          t(
            translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.isGlobal()
          )
        ),
      courseOfStudyIds: yup
        .array()
        .of(yup.number().transform(value => (isNaN(value) ? null : value)))
        .when('isGlobal', {
          is: true,
          then: yup.array(),
          otherwise: yup.array().when('excludeCourses', {
            is: true,
            then: yup.array(),
            otherwise: yup.array().min(1, t(translations.validation.minOne()))
          })
        }),

      prioritization: yup
        .array()
        .of(
          yup.object().shape({
            semester: yup.string()
          })
        )
        .label(
          t(
            translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.prioritization()
          )
        ),
      seats: yup
        .number()
        .label(
          t(
            translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.seats()
          )
        )
        .transform(value => (isNaN(value) ? null : value))
        .nullable(),
      hasAutomaticSeatDistribution: yup
        .boolean()
        .label(
          t(translations.pages.modules.create.prioritizationAndApproval.automaticSeatDistribution())
        )
        .nullable()
    })
  ),
  responsibleCourseOfStudy: yup.number().when('subjectType', {
    is: SubjectType.Fwpm,
    then: yup
      .number()
      .required(t(translations.validation.required()))
      .nullable()
      .label(t(translations.pages.modules.faculty()))
      .transform(value => (isNaN(value) ? null : value)),
    otherwise: yup.number().nullable()
  }),
  sorting: yup.string().nullable(),
  subjectType: yup.string().oneOf(['AWPM', 'FWPM']).required(t(translations.validation.required()))
});

export const moduleInSelectionValidationSchema = t => ({
  attachments: yup.mixed(),
  description: yup.string().nullable(),
  groupingId: yup.number().when('subjectType', {
    is: 'AWPM',
    then: yup
      .number()
      .required(t(translations.validation.required()))
      .nullable()
      .label(t(translations.pages.groupings.modal.groupingName()))
      .transform(value => (isNaN(value) ? null : value)),
    otherwise: yup.number().nullable()
  }),
  moduleOfficer: yup.number().required().nullable(),
  lecturers: yup.array().of(yup.number().nullable()),
  unregisteredLecturers: yup.array().of(yup.string().nullable()),
  isMoveUpActive: yup.boolean(),
  isTakingPlace: yup.boolean(),

  maxParticipants: yup
    .number()
    .transform(value => (isNaN(value) ? null : value))
    .nullable()
    .required(t(translations.validation.required()))
    .label(t(translations.pages.modules.create.periodsAndParticipants.participants.max())),
  minParticipants: yup
    .number()
    .nullable()
    .transform(value => (isNaN(value) ? null : value)),
  name: yup
    .string()
    .nullable()
    .required(t(translations.validation.required()))
    .label(t(translations.pages.modules.create.GeneralInfo.name())),
  participantsConfigurations: yup
    .array()
    .of(
      yup.object().shape({
        custom: yup.boolean(),
        admissionCriteria: yup
          .array()
          .of(
            yup
              .string()
              .oneOf(['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'B8', 'B9', 'M1', 'M2', 'M3'])
          )
          .min(1, t(translations.validation.minOne()))
          .label(
            t(
              translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.admissionCriteria()
            )
          ),
        isGlobal: yup
          .boolean()
          .nullable()
          .transform(value => (!value ? false : true))
          .label(
            t(
              translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.isGlobal()
            )
          ),
        courseOfStudyIds: yup
          .array()
          .of(yup.number().transform(value => (isNaN(value) ? null : value)))
          .when('isGlobal', {
            is: true,
            then: yup.array(),
            otherwise: yup.array().when('excludeCourses', {
              is: true,
              then: yup.array(),
              otherwise: yup.array().min(1, t(translations.validation.minOne()))
            })
          }),
        prioritization: yup
          .array()
          .of(
            yup.object().shape({
              semester: yup.string()
            })
          )
          .min(1, t(translations.validation.minOne()))
          .label(
            t(
              translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.prioritization()
            )
          ),
        seats: yup
          .number()
          .nullable()
          .label(
            t(
              translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.seats()
            )
          ),
        hasAutomaticSeatDistribution: yup
          .boolean()
          .label(
            t(
              translations.pages.modules.create.prioritizationAndApproval.automaticSeatDistribution()
            )
          )
          .nullable()
      })
    )
    .min(1),
  sorting: yup.string().nullable(),
  responsibleCourseOfStudy: yup.number().when('subjectType', {
    is: SubjectType.Fwpm,
    then: yup
      .number()
      .required(t(translations.validation.required()))
      .nullable()
      .label(t(translations.pages.modules.faculty()))
      .transform(value => (isNaN(value) ? null : value)),
    otherwise: yup.number().nullable()
  }),
  subjectType: yup.string().oneOf(['AWPM', 'FWPM']).required(t(translations.validation.required()))
});
export const moduleInitialValues = (modules, subjectType: SubjectType) =>
  modules
    ? {
        ...pick(modules, [
          'id',
          'attachments',
          'description',
          'moduleOfficer',
          'lecturers',
          'unregisteredLecturers',
          'isMoveUpActive',
          'isTakingPlace',
          'maxParticipants',
          'minParticipants',
          'name',
          'participantsConfigurations',
          'sorting',
          'subjectType',
          'responsibleCourseOfStudy',
          'distributeEvenly',
          'fillUpByKey',
          'isDefault',
          'lecturersUsers'
        ]),
        groupingId: modules.subjectType === SubjectType.Awpm ? modules?.grouping?.id : null
      }
    : {
        attachments: [],
        description: '',
        moduleOfficer: null,
        lecturers: [],
        unregisteredLecturers: [],
        groupingId: null,
        info: null,
        isMoveUpActive: true,
        isTakingPlace: true,
        maxParticipants: null,
        minParticipants: null,
        name: '',
        participantsConfigurations: [],
        responsibleCourseOfStudy: null,
        distributeEvenly: false,
        fillUpByKey: true,
        sorting: ' ',
        subjectType,
        timeframe: null
      };
